import React from 'react';
import { Badge as AntBadge, BadgeProps as AntBadgeProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface BadgeProps extends AntBadgeProps {
  className?: string;
}

export interface RibbonProps extends React.ComponentProps<typeof AntBadge.Ribbon> {
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  badge: {
    '& .ant-badge-count': {
      backgroundColor: theme.color.semantic.brand.default.primary,
      color: theme.color.semantic.text.inverse,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      boxShadow: 'none',
      borderRadius: theme.radius.lg,
      minWidth: '20px',
      height: '20px',
      lineHeight: '20px',
      padding: `${theme.spacing[0]} ${theme.spacing[2]}`,
    },
    '& .ant-badge-count-sm': {
      minWidth: '16px',
      height: '16px',
      lineHeight: '16px',
      fontSize: theme.typography.fontSize.xs,
      padding: `${theme.spacing[0]} ${theme.spacing[1]}`,
    },
    '& .ant-badge-dot': {
      backgroundColor: theme.color.semantic.brand.danger.primary,
      boxShadow: 'none',
      width: '6px',
      height: '6px',
    },
    '& .ant-badge-status-dot': {
      width: '6px',
      height: '6px',
    },
    '& .ant-badge-status-success': {
      backgroundColor: theme.color.semantic.accent.green.strong,
    },
    '& .ant-badge-status-processing': {
      backgroundColor: theme.color.semantic.accent.blue.strong,
    },
    '& .ant-badge-status-default': {
      backgroundColor: theme.color.semantic.text.tertiary,
    },
    '& .ant-badge-status-error': {
      backgroundColor: theme.color.semantic.brand.danger.primary,
    },
    '& .ant-badge-status-warning': {
      backgroundColor: theme.color.semantic.accent.yellow.strong,
    },
    '& .ant-badge-status-text': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      marginLeft: theme.spacing[2],
    },
  },
  ribbon: {
    '& .ant-ribbon': {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
    },
    '& .ant-ribbon-text': {
      color: theme.color.semantic.text.inverse,
    },
  },
  // Color overrides using design tokens
  colorPink: {
    '& .ant-badge-count, & .ant-scroll-number-only-unit': {
      backgroundColor: `${theme.color.semantic.accent.pink.strong} !important`,
    },
  },
  colorRed: {
    '& .ant-badge-count, & .ant-scroll-number-only-unit': {
      backgroundColor: `${theme.color.semantic.accent.red.strong} !important`,
    },
  },
  colorYellow: {
    '& .ant-badge-count, & .ant-scroll-number-only-unit': {
      backgroundColor: `${theme.color.semantic.accent.yellow.strong} !important`,
    },
  },
  colorOrange: {
    '& .ant-badge-count, & .ant-scroll-number-only-unit': {
      backgroundColor: `${theme.color.semantic.accent.orange.strong} !important`,
    },
  },
  colorCyan: {
    '& .ant-badge-count, & .ant-scroll-number-only-unit': {
      backgroundColor: `${theme.color.semantic.accent.cyan.strong} !important`,
    },
  },
  colorGreen: {
    '& .ant-badge-count, & .ant-scroll-number-only-unit': {
      backgroundColor: `${theme.color.semantic.accent.green.strong} !important`,
    },
  },
  colorBlue: {
    '& .ant-badge-count, & .ant-scroll-number-only-unit': {
      backgroundColor: `${theme.color.semantic.brand.default.light} !important`,
      color: `${theme.color.semantic.brand.default.primary} !important`,
    },
  },
  colorPurple: {
    '& .ant-badge-count, & .ant-scroll-number-only-unit': {
      backgroundColor: `${theme.color.semantic.accent.purple.strong} !important`,
    },
  },
  colorGeekblue: {
    '& .ant-badge-count, & .ant-scroll-number-only-unit': {
      backgroundColor: `${theme.color.semantic.accent.purple.strong} !important`,
    },
  },
  colorMagenta: {
    '& .ant-badge-count, & .ant-scroll-number-only-unit': {
      backgroundColor: `${theme.color.semantic.accent.pink.strong} !important`,
    },
  },
  colorVolcano: {
    '& .ant-badge-count, & .ant-scroll-number-only-unit': {
      backgroundColor: `${theme.color.semantic.accent.red.strong} !important`,
    },
  },
  colorGold: {
    '& .ant-badge-count, & .ant-scroll-number-only-unit': {
      backgroundColor: `${theme.color.semantic.accent.yellow.strong} !important`,
    },
  },
  colorLime: {
    '& .ant-badge-count, & .ant-scroll-number-only-unit': {
      backgroundColor: `${theme.color.semantic.accent.green.bold} !important`,
    },
  },
  // Ribbon color overrides
  ribbonColorPink: {
    '& .ant-ribbon': {
      backgroundColor: `${theme.color.semantic.accent.pink.strong} !important`,
    },
  },
  ribbonColorRed: {
    '& .ant-ribbon': {
      backgroundColor: `${theme.color.semantic.accent.red.strong} !important`,
    },
  },
  ribbonColorYellow: {
    '& .ant-ribbon': {
      backgroundColor: `${theme.color.semantic.accent.yellow.strong} !important`,
    },
  },
  ribbonColorOrange: {
    '& .ant-ribbon': {
      backgroundColor: `${theme.color.semantic.accent.orange.strong} !important`,
    },
  },
  ribbonColorCyan: {
    '& .ant-ribbon': {
      backgroundColor: `${theme.color.semantic.accent.cyan.strong} !important`,
    },
  },
  ribbonColorGreen: {
    '& .ant-ribbon': {
      backgroundColor: `${theme.color.semantic.accent.green.strong} !important`,
    },
  },
  ribbonColorBlue: {
    '& .ant-ribbon': {
      backgroundColor: `${theme.color.semantic.accent.blue.strong} !important`,
    },
  },
  ribbonColorPurple: {
    '& .ant-ribbon': {
      backgroundColor: `${theme.color.semantic.accent.purple.strong} !important`,
    },
  },
  ribbonColorGeekblue: {
    '& .ant-ribbon': {
      backgroundColor: `${theme.color.semantic.accent.purple.strong} !important`,
    },
  },
  ribbonColorMagenta: {
    '& .ant-ribbon': {
      backgroundColor: `${theme.color.semantic.accent.pink.strong} !important`,
    },
  },
  ribbonColorVolcano: {
    '& .ant-ribbon': {
      backgroundColor: `${theme.color.semantic.accent.red.strong} !important`,
    },
  },
  ribbonColorGold: {
    '& .ant-ribbon': {
      backgroundColor: `${theme.color.semantic.accent.yellow.strong} !important`,
    },
  },
  ribbonColorLime: {
    '& .ant-ribbon': {
      backgroundColor: `${theme.color.semantic.accent.green.bold} !important`,
    },
  },
}));

const BadgeComponent: React.FC<BadgeProps> = ({
  className = '',
  color,
  children,
  ...rest
}) => {
  const classes = useStyles();

  const getColorClass = () => {
    if (!color || typeof color !== 'string') return '';

    const colorMap: Record<string, string> = {
      pink: classes.colorPink,
      red: classes.colorRed,
      yellow: classes.colorYellow,
      orange: classes.colorOrange,
      cyan: classes.colorCyan,
      green: classes.colorGreen,
      blue: classes.colorBlue,
      purple: classes.colorPurple,
      geekblue: classes.colorGeekblue,
      magenta: classes.colorMagenta,
      volcano: classes.colorVolcano,
      gold: classes.colorGold,
      lime: classes.colorLime,
    };

    return colorMap[color.toLowerCase()] || '';
  };

  const combinedClassName = `${classes.badge} ${getColorClass()} ${className}`.trim();

  return (
    <AntBadge
      {...rest}
      color={color}
      className={combinedClassName}
    >
      {children}
    </AntBadge>
  );
};

const RibbonComponent: React.FC<RibbonProps> = ({
  className = '',
  color,
  children,
  ...rest
}) => {
  const classes = useStyles();

  const getRibbonColorClass = () => {
    if (!color || typeof color !== 'string') return '';

    const colorMap: Record<string, string> = {
      pink: classes.ribbonColorPink,
      red: classes.ribbonColorRed,
      yellow: classes.ribbonColorYellow,
      orange: classes.ribbonColorOrange,
      cyan: classes.ribbonColorCyan,
      green: classes.ribbonColorGreen,
      blue: classes.ribbonColorBlue,
      purple: classes.ribbonColorPurple,
      geekblue: classes.ribbonColorGeekblue,
      magenta: classes.ribbonColorMagenta,
      volcano: classes.ribbonColorVolcano,
      gold: classes.ribbonColorGold,
      lime: classes.ribbonColorLime,
    };

    return colorMap[color.toLowerCase()] || '';
  };

  const combinedClassName = `${classes.ribbon} ${getRibbonColorClass()} ${className}`.trim();

  return (
    <AntBadge.Ribbon
      {...rest}
      color={color}
      className={combinedClassName}
    >
      {children}
    </AntBadge.Ribbon>
  );
};

export const Badge = Object.assign(BadgeComponent, {
  Ribbon: RibbonComponent,
});

export default Badge;
